/** Questions types pour notation des 5 études de faisabilité (score front → API). */

import {
  FEASIBILITY_LABELS,
  FEASIBILITY_VIABILITY_THRESHOLD,
  type FeasibilityType,
  viabilityFromScore,
} from './constants';
import type { EvaluationConclusionReport } from './types';

export interface FeasibilityQuestion {
  id: string;
  label: string;
  /** Poids max de la question (somme des poids ≈ contribution au score de l'étude). */
  maxPoints: number;
}

export const FEASIBILITY_QUESTIONS: Record<FeasibilityType, FeasibilityQuestion[]> = {
  technical: [
    { id: 'tech_1', label: 'La solution est-elle techniquement réalisable aujourd’hui ?', maxPoints: 20 },
    { id: 'tech_2', label: 'Disposez-vous des compétences / partenaires techniques ?', maxPoints: 20 },
    { id: 'tech_3', label: 'Le délai de mise en œuvre est-il réaliste ?', maxPoints: 15 },
    { id: 'tech_4', label: 'Les infrastructures nécessaires sont-elles accessibles ?', maxPoints: 15 },
    { id: 'tech_5', label: 'Le niveau d’innovation technique est-il maîtrisé ?', maxPoints: 15 },
  ],
  legal: [
    { id: 'leg_1', label: 'Le cadre réglementaire est-il clair pour cette activité ?', maxPoints: 20 },
    { id: 'leg_2', label: 'Les licences / autorisations sont-elles obtenables ?', maxPoints: 20 },
    { id: 'leg_3', label: 'La propriété intellectuelle est-elle sécurisable ?', maxPoints: 15 },
    { id: 'leg_4', label: 'Les contrats fournisseurs/clients sont-ils standardisables ?', maxPoints: 15 },
    { id: 'leg_5', label: 'Les risques juridiques majeurs sont-ils limités ?', maxPoints: 15 },
  ],
  financial: [
    { id: 'fin_1', label: 'Le besoin de financement initial est-il estimé ?', maxPoints: 20 },
    { id: 'fin_2', label: 'Les sources de financement sont-elles identifiées ?', maxPoints: 20 },
    { id: 'fin_3', label: 'Le seuil de rentabilité est-il atteignable en 24 mois ?', maxPoints: 15 },
    { id: 'fin_4', label: 'La structure de coûts est-elle maîtrisée ?', maxPoints: 15 },
    { id: 'fin_5', label: 'Les cash-flows prévisionnels sont-ils cohérents ?', maxPoints: 15 },
  ],
  commercial: [
    { id: 'com_1', label: 'Le marché cible est-il suffisant et accessible ?', maxPoints: 20 },
    { id: 'com_2', label: 'La proposition de valeur est-elle différenciante ?', maxPoints: 20 },
    { id: 'com_3', label: 'Les canaux d’acquisition sont-ils identifiés ?', maxPoints: 15 },
    { id: 'com_4', label: 'Le prix est-il compatible avec le pouvoir d’achat ?', maxPoints: 15 },
    { id: 'com_5', label: 'La concurrence laisse-t-elle une place viable ?', maxPoints: 15 },
  ],
  organizational: [
    { id: 'org_1', label: 'L’équipe fondatrice couvre-t-elle les rôles clés ?', maxPoints: 20 },
    { id: 'org_2', label: 'Les processus opérationnels sont-ils définissables ?', maxPoints: 20 },
    { id: 'org_3', label: 'La gouvernance proposée est-elle claire ?', maxPoints: 15 },
    { id: 'org_4', label: 'Le plan de recrutement est-il réaliste ?', maxPoints: 15 },
    { id: 'org_5', label: 'La culture / valeurs soutiennent-elles l’exécution ?', maxPoints: 15 },
  ],
};

/** Note utilisateur 0–5 transformée en points (note/5 * maxPoints). */
export function scoreFromAnswers(
  type: FeasibilityType,
  answers: Record<string, number>,
): number {
  const questions = FEASIBILITY_QUESTIONS[type];
  let total = 0;
  for (const q of questions) {
    const note = Math.min(5, Math.max(0, Number(answers[q.id]) || 0));
    total += (note / 5) * q.maxPoints;
  }
  return Math.round(total);
}

/** Max théorique par type (somme des maxPoints). */
export function maxScoreForType(type: FeasibilityType): number {
  return FEASIBILITY_QUESTIONS[type].reduce((s, q) => s + q.maxPoints, 0);
}

/**
 * Conclusions CDC (même format viable / non viable) :
 * Résultats, points forts, points faibles, recommandations.
 */
export function buildFeasibilityConclusionReport(
  scores: Record<FeasibilityType, number>,
): EvaluationConclusionReport {
  const entries = (Object.keys(FEASIBILITY_LABELS) as FeasibilityType[]).map((t) => {
    const score = scores[t] ?? 0;
    const max = maxScoreForType(t);
    const pct = max > 0 ? (score / max) * 100 : 0;
    return { type: t, label: FEASIBILITY_LABELS[t], score, max, pct };
  });
  const sum = entries.reduce((a, e) => a + e.score, 0);
  const viability = viabilityFromScore(sum);
  const sortedAsc = [...entries].sort((a, b) => a.pct - b.pct);
  const sortedDesc = [...entries].sort((a, b) => b.pct - a.pct);

  const strengths = sortedDesc
    .filter((e) => e.pct >= 65)
    .slice(0, 3)
    .map((e) => `${e.label} : ${e.score}/${e.max} (${Math.round(e.pct)} %)`);
  if (!strengths.length && sortedDesc[0]) {
    strengths.push(
      `${sortedDesc[0].label} est la dimension la plus solide (${sortedDesc[0].score}/${sortedDesc[0].max}).`,
    );
  }

  const weaknesses = sortedAsc
    .filter((e) => e.pct < 65)
    .slice(0, 3)
    .map((e) => `${e.label} : ${e.score}/${e.max} (${Math.round(e.pct)} %) — à renforcer`);
  if (!weaknesses.length && sortedAsc[0]) {
    weaknesses.push(`Aucune faiblesse majeure ; surveiller ${sortedAsc[0].label}.`);
  }

  const recommendations = sortedAsc.slice(0, 3).map((e) => {
    if (e.pct < 50) {
      return `Priorité haute : élaborer un plan d’actions sur la faisabilité ${e.label.toLowerCase()}.`;
    }
    if (e.pct < 65) {
      return `Consolider la dimension ${e.label.toLowerCase()} avant le déploiement.`;
    }
    return `Maintenir le niveau de la dimension ${e.label.toLowerCase()} et documenter les preuves.`;
  });

  if (viability === 'not_viable') {
    recommendations.unshift(
      `Score ${sum} inférieur au seuil de ${FEASIBILITY_VIABILITY_THRESHOLD} : retravailler les axes faibles avant de conclure à la viabilité.`,
    );
  } else {
    recommendations.unshift(
      `Score ${sum} ≥ ${FEASIBILITY_VIABILITY_THRESHOLD} : le projet est jugé viable ; poursuivre la modélisation (BMC / business plan).`,
    );
  }

  const results =
    `Étude de faisabilité — score final ${sum} points ` +
    `(seuil de viabilité ${FEASIBILITY_VIABILITY_THRESHOLD}). ` +
    `Conclusion : projet ${viability === 'viable' ? 'viable' : 'non viable'}. ` +
    entries.map((e) => `${e.label} ${e.score}/${e.max}`).join(' · ');

  return { results, strengths, weaknesses, recommendations, viability };
}

export function formatConclusionReportText(
  title: string,
  report: EvaluationConclusionReport,
  meta?: Record<string, string>,
): string {
  const lines = [title, ''];
  if (meta) {
    for (const [k, v] of Object.entries(meta)) {
      lines.push(`${k} : ${v}`);
    }
    lines.push('');
  }
  lines.push(
    `Statut : ${report.viability === 'viable' ? 'Viable' : report.viability === 'not_viable' ? 'Non viable' : 'En attente'}`,
    '',
    '1. Résultats',
    report.results,
    '',
    '2. Points forts',
    ...(report.strengths.length ? report.strengths.map((s) => `• ${s}`) : ['• —']),
    '',
    '3. Points faibles',
    ...(report.weaknesses.length ? report.weaknesses.map((s) => `• ${s}`) : ['• —']),
    '',
    '4. Recommandations',
    ...(report.recommendations.length ? report.recommendations.map((s) => `• ${s}`) : ['• —']),
    '',
  );
  return lines.join('\n');
}

export const SCAMPER_KEYS = [
  { key: 'substitute', label: 'S — Substitute', prompt: 'Que peut-on substituer ?' },
  { key: 'combine', label: 'C — Combine', prompt: 'Que peut-on combiner ?' },
  { key: 'adapt', label: 'A — Adapt', prompt: 'Que peut-on adapter ?' },
  { key: 'modify', label: 'M — Modify / Magnify', prompt: 'Que peut-on modifier ou amplifier ?' },
  { key: 'put_to_other_use', label: 'P — Put to other uses', prompt: 'Autres usages possibles ?' },
  { key: 'eliminate', label: 'E — Eliminate', prompt: 'Que peut-on éliminer ?' },
  { key: 'reverse', label: 'R — Reverse / Rearrange', prompt: 'Que peut-on inverser ou réorganiser ?' },
] as const;

export type ScamperKey = (typeof SCAMPER_KEYS)[number]['key'];

export const BUSINESS_PLAN_FIELDS = [
  { key: 'project_name', label: 'Nom du projet / entreprise', section: 'Général' },
  { key: 'sector', label: 'Secteur d’activité', section: 'Général' },
  { key: 'location', label: 'Localisation', section: 'Général' },
  { key: 'vision', label: 'Vision', section: 'Présentation' },
  { key: 'mission', label: 'Mission', section: 'Présentation' },
  { key: 'probleme_identifie', label: 'Problème / besoin', section: 'Présentation' },
  { key: 'solution_proposee', label: 'Solution proposée', section: 'Présentation' },
  { key: 'valeur_ajoutee', label: 'Valeur ajoutée', section: 'Présentation' },
  { key: 'taille_marche', label: 'Taille du marché', section: 'Marché' },
  { key: 'segments_clientele', label: 'Segments clientèle', section: 'Marché' },
  { key: 'concurrents_principaux', label: 'Concurrents', section: 'Marché' },
  { key: 'avantage_concurrentiel', label: 'Avantage concurrentiel', section: 'Marché' },
  { key: 'description_offre', label: 'Description de l’offre', section: 'Offre' },
  { key: 'sources_revenus', label: 'Sources de revenus', section: 'Modèle économique' },
  { key: 'politique_prix', label: 'Politique de prix', section: 'Modèle économique' },
  { key: 'strategie_acquisition', label: 'Stratégie d’acquisition', section: 'Commercial' },
  { key: 'canaux_distribution', label: 'Canaux de distribution', section: 'Commercial' },
  { key: 'equipe_fondatrice', label: 'Équipe fondatrice', section: 'Organisation' },
  {
    key: 'prevision_ventes_ca',
    label: 'Prévision de ventes — CA (N1, N2, N3)',
    section: 'Prévisions financières (année 1 à 3)',
  },
  {
    key: 'prevision_charges',
    label: 'Prévision de charges (N1, N2, N3)',
    section: 'Prévisions financières (année 1 à 3)',
  },
  {
    key: 'resultats_previsionnels',
    label: 'Résultats prévisionnels (N1, N2, N3)',
    section: 'Prévisions financières (année 1 à 3)',
  },
  {
    key: 'besoins_financement',
    label: 'Montant recherché',
    section: 'Besoin de financement (Montant recherché, utilisation des fonds)',
  },
  {
    key: 'utilisation_fonds',
    label: 'Utilisation des fonds',
    section: 'Besoin de financement (Montant recherché, utilisation des fonds)',
  },
  { key: 'risques_mesures', label: 'Risques et mesures', section: 'Risques' },
] as const;

export function emptyBusinessPlanContent(): Record<string, string> {
  return Object.fromEntries(BUSINESS_PLAN_FIELDS.map((f) => [f.key, '']));
}
